import React, { useContext, useEffect, useState, useRef } from "react";
import GlobalConfig from "../../context/GlobalConfig";
import Footer from "../../components/Footer";
import DocumentsList from "../../components/DocumentsList";
import Head from "../Head";
import { languageData } from "../../constants/languageData";
import { countries } from "../../constants/countries";
import JourneyConfig from "../../context/JourneyConfig";
import { ChevronLeftIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import "./selectDocument.css";
import { useParams, useNavigate } from "react-router-dom";
import RenderSvg from "../../components/RenderSvg";
import Loader from "../../components/Loader/Loader";
import { noDocReplacements, secondDocReplacements } from "../../constants/documentSideConfigConstants";
import { getDocumentCountry, getDocumentFieldName, getDocumentIdType } from "../../utils/documentFields";
import { findLastRoute } from "../../utils/orchestrationModule";

/** Flow-config name of the grouped document type that opens a second-level list. */
const OTHER_ID_CARD = "Other Id Card";
/** Below this many entries the list is short enough to scan, so the search box is more chrome than help. */
const OTHER_ID_SEARCH_THRESHOLD = 10;

function SelectDocument({
  stepperFunc,
  steps,
  stepHistory,
  currentJourneyState,
  firstVerifiedDocumentName,
  docIteration,
  stepObj,
  currentDocumentRetries,
  currentDocumentStep,
  currentStep
}) {
  const hasUpdated = useRef(false);
  // IDX-707: fires the one-time Digilocker auto-redirect (no Document Selection screen).
  const digilockerRedirectedRef = useRef(false);
  const { journeyId } = useParams();
  const navigate = useNavigate();
  const { config, setConfig, routesAndNavigation } = useContext(GlobalConfig);
  const { journey, setJourney, updateJourney, getJourney } =
    useContext(JourneyConfig);
  const { branding } = config ?? {};
  const documentCapture = Array.isArray(config?.documentCapture)
    ? config?.documentCapture[currentDocumentStep]
    : config?.documentCapture;
  const { capturedData: journeyData } = journey ?? {};
  // This document's own country, not the journey's first (IDX-677). getDocumentCountry resolves in
  // priority order — this document's captured country, then its prefilled country, then the
  // flow-wide `country` (back-compat) — so a prefilled document 2 whose country screen was skipped
  // uses userInfo.id.country2 instead of document 1's country. A single-country flow stores nothing
  // per document, so the helper falls back to `country` and this resolves exactly as before.
  const countryCode =
    getDocumentCountry(journey, currentDocumentStep) ||
    branding?.countrySelected?.value ||
    sessionStorage.getItem("selectedCountry");
  const language = sessionStorage.getItem("language") ?? "en";
  const [areRoutesLoaded, setAreRoutesLoaded] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    countryCode
      ? countries.find((country) => country.value === countryCode)
      : null
  );

  const [documentLists, setDocumentLists] = useState(
    documentCapture?.countries.find(
      (eachCountry) => eachCountry.country === selectedCountry?.label
    )
  );
  const [documents, setDocuments] = useState([]);

  // "Other ID Card" covers a set of concrete documents that used to be invisible to the
  // applicant. Tapping it opens that set in this same list rather than advancing, so no new
  // route or journey step is involved — the drill-down is purely local view state.
  const [isOtherIdDrillDown, setIsOtherIdDrillDown] = useState(false);
  const [otherIdSearch, setOtherIdSearch] = useState("");

  // Ensure session has the correct document step when entering second document selection
  useEffect(() => {
    const stored = parseInt(sessionStorage.getItem("currentDocumentStep"));
    if (firstVerifiedDocumentName && (isNaN(stored) || stored === 0)) {
      sessionStorage.setItem("currentDocumentStep", "1");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstVerifiedDocumentName]);

  useEffect(() => {
    if (selectedCountry && journeyData) {
      const documentLists = documentCapture?.countries.find(
        (eachCountry) => eachCountry.country === selectedCountry?.label
      );

      setDocuments(() => {
        const idObject = journey?.userInfo?.id;
        const idTypeKey = `idType${currentDocumentStep === 0 ? "" : currentDocumentStep + 1
          }`;

        // Case 1: If we already have a stored ID type, just return it
        if (idObject?.[idTypeKey]) {
          return [idObject[idTypeKey]];
        }

        // Case 2: Build the allowedDocs + noDocList merged array
        const allowedDocs = documentLists?.documentsAllowed ?? [];
        let noDocList = documentLists?.noDoc ?? [];

        // Replace Aadhaar → Digilocker, PAN → PAN Verification
        const transformedNoDocList = noDocList.map(doc => {
          return noDocReplacements[doc]?.uiLabel || doc;
        });


        // Merge allowedDocs and transformed noDocList
        let mergedDocs = [...allowedDocs, ...transformedNoDocList];

        // Add "International Passport" if enabled
        if (documentCapture?.internationalPassport) {
          mergedDocs.push("International Passport");
        }

        // Remove every document the applicant has ALREADY captured, so the same ID cannot be
        // submitted twice. Only document 1 used to be excluded, which was enough while journeys
        // captured two documents — on a third document it left document 2's ID on the list, and
        // the applicant only found out it was a duplicate after capturing it (the backend rejects
        // it as DOCTYPE_FALSE, burning a retry).
        //
        // The backend publishes the full list on every DOC_CHECK (currentJourneyState /
        // verifiedDocumentNames), and Analyzing persists it here. It cannot be read from
        // journey.capturedData: the journey object is fetched once at startup and never refreshed
        // during capture, so capturedData.idType2 is simply not there when this screen renders.
        //
        // Falls back to firstVerifiedDocumentName for journeys that were already in flight before
        // the list existed — those behave exactly as they did before.
        const verifiedDocuments = (() => {
          try {
            const stored = JSON.parse(sessionStorage.getItem("verifiedDocuments"));
            if (Array.isArray(stored)) return stored;
          } catch (parseError) {
            console.warn("[SelectDocument] Could not read verifiedDocuments", parseError);
          }
          // A new tab or device starts with empty sessionStorage; the backend keeps the same list.
          const fromBackend = currentJourneyState?.verifiedDocuments;
          return Array.isArray(fromBackend) ? fromBackend : [];
        })();

        const alreadyCapturedDocs = (() => {
          // A type is a duplicate only for the same country; International Passport has no country.
          // A document with no recorded country still counts, as it did before countries were known.
          if (verifiedDocuments.length > 0) {
            return verifiedDocuments
              .filter((verified) =>
                !verified?.country ||
                verified.country === selectedCountry?.value ||
                verified?.idType === "International Passport"
              )
              .map((verified) => verified.idType);
          }
          try {
            const stored = JSON.parse(sessionStorage.getItem("verifiedDocumentNames"));
            if (Array.isArray(stored) && stored.length > 0) return stored;
          } catch (parseError) {
            console.warn("[SelectDocument] Could not read verifiedDocumentNames", parseError);
          }
          return firstVerifiedDocumentName ? [firstVerifiedDocumentName] : [];
        })();

        // Any earlier passport could return as International Passport, which the backend does not deduplicate.
        const passportAlreadyCaptured = verifiedDocuments.some(
          (verified) => verified?.idType === "Passport" || verified?.idType === "International Passport"
        );

        mergedDocs = mergedDocs.filter((item) => {
          if (item === "International Passport" && passportAlreadyCaptured) return false;
          if (!alreadyCapturedDocs.length) return true;

          // The document itself...
          if (alreadyCapturedDocs.includes(item)) return false;

          // ...and any equivalent of it (Aadhaar ⇄ Digilocker, PAN ⇄ PAN Verification), so the
          // same underlying document cannot come back through the other route.
          const blockedDocs = alreadyCapturedDocs.flatMap(
            (doc) => secondDocReplacements[doc] || []
          );

          return !blockedDocs.includes(item);
        });


        mergedDocs.sort((a, b) => a.localeCompare(b));

        return mergedDocs;

      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry, journeyData]);


  const [loading, setLoading] = useState(
    Object.keys(config).length && Object.keys(journey).length ? false : true
  );
  // Only the chosen document type lives here; the country is read per-document via getDocumentCountry
  // above. A `country` copy here would be the unsuffixed document-1 value and is never read (IDX-677).
  const [currentPageState, setCurrentPageState] = useState({ idType: "" });

  // When the document type for this step is prefilled (dashboard prefill), the picker resolves
  // to a single, already-known option. Skip that redundant selection screen and proceed exactly
  // as a manual tap would — straight to capture (DOC) or Digilocker (NO_DOC).
  const autoSelectedRef = useRef(false);
  const prefilledIdTypeKey = `idType${currentDocumentStep === 0 ? "" : currentDocumentStep + 1}`;
  const prefilledDocType = journey?.userInfo?.id?.[prefilledIdTypeKey];
  // Don't auto-select right after returning from Digilocker — that document is already handled,
  // and re-selecting it would re-launch Digilocker and loop.
  const isReturningFromDigilocker =
    new URLSearchParams(window.location.search).get("source") === "Digilocker";
  // On a Digilocker flow the whole document phase is the bundle (auto-redirect handles it); never
  // auto-select a prefilled doc here, or it could fire a second DOC_SELECTION alongside the redirect.
  const willAutoSelectPrefilledDoc =
    !isReturningFromDigilocker && !config?.digilocker?.required && !!prefilledDocType && documents.length === 1 && documents[0] === prefilledDocType;
  const [height, setHeight] = useState(window.innerHeight);
const [width, setWidth] = useState(window.innerWidth);

const isMobileView = height <= 800 && width <= 720;

useEffect(() => {
  const handleResize = () => {
    setHeight(window.innerHeight);
    setWidth(window.innerWidth);
  };
  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);



  useEffect(() => {
    if (Object.keys(config).length && Object.keys(journey).length && loading) {
      setJourney(journey);
      const selectedCountry = countries.find(
        (country) =>
          country.value === getDocumentCountry(journey, currentDocumentStep)
      );
      const updatedConfig = {
        ...config,
        branding: {
          ...branding,
          countrySelected: selectedCountry,
        },
      };
      const documentCapture = Array.isArray(updatedConfig?.documentCapture)
        ? updatedConfig?.documentCapture[currentDocumentStep]
        : updatedConfig?.documentCapture;
      // Persist currentDocumentStep defensively so route/config refreshes don't reset it
      if (typeof currentDocumentStep === "number" && !isNaN(currentDocumentStep)) {
        sessionStorage.setItem("currentDocumentStep", String(currentDocumentStep));
      }
      setConfig(updatedConfig);
      setSelectedCountry(selectedCountry);
      setDocumentLists(
        documentCapture?.countries.find(
          (eachCountry) => eachCountry.country === selectedCountry?.label
        )
      );
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  useEffect(() => {
    if (journeyId && !journey) getJourney(journeyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId]);

  useEffect(() => {
    if (areRoutesLoaded) {
      stepperFunc(steps[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areRoutesLoaded]);

  // Back goes to the country screen this document was chosen from: the nearest one at or before
  // the current step. Scanning the whole route map for the last country screen instead only
  // matched while the journey had exactly one; with a country screen per document (IDX-677) it
  // sends document 1 forwards into document 2's screen.
  const handleBackButtonClick = () => {
    // Inside the Other ID list, back means "up one level", not "back to country selection".
    if (isOtherIdDrillDown) {
      closeOtherIdDrillDown();
      return;
    }
    const countryRoute = findLastRoute(stepObj, currentStep, "country");
    const fallback = stepHistory?.length >= 2 ? stepHistory[stepHistory.length - 2] : 1;
    stepperFunc(countryRoute ? parseInt(countryRoute[0]) : fallback);
  };
  const resolveIndiaNoDoc = ({
    uiIdType,
    documentCapture,
    noDocReplacements,
  }) => {
    const noDocForIndia =
      documentCapture?.countries?.find(c => c.country === "India")?.noDoc || [];

    // Build Map: UI value → backend value
    const indiaNoDocMap = new Map(
      noDocForIndia.map(doc => [
        noDocReplacements[doc]?.uiLabel || doc,
        noDocReplacements[doc]?.storedValue || doc,
      ])
    );

    return {
      backendIdType: indiaNoDocMap.get(uiIdType) || uiIdType,
      isNoDoc: indiaNoDocMap.has(uiIdType),
    };
  };

  const updateJourneyAsync = async () => {
      if (!currentPageState?.idType) return;

      const { backendIdType, isNoDoc } = resolveIndiaNoDoc({
        uiIdType: currentPageState.idType,
        documentCapture,
        noDocReplacements,
      });

      sessionStorage.setItem("document", backendIdType);

      const verificationType = isNoDoc ? "NO_DOC" : "DOC";

      const response = await updateJourney(
        "DOC_SELECTION",
        {
          currentDocumentStep: currentDocumentStep + 1,
          idType: backendIdType,
          verificationType,
        },
        currentStep
      );

      console.log(
        "verificationType:",
        verificationType,
        "UI idType:",
        currentPageState.idType,
        "Backend idType:",
        backendIdType
      );

      if (verificationType === "NO_DOC" && response?.digilockerUrl) {
        sessionStorage.setItem("digilockerUrl", response.digilockerUrl);
        window.location.replace(response.digilockerUrl);
        return;
      }

      const correctedStep =
        firstVerifiedDocumentName &&
        (!currentDocumentStep || isNaN(currentDocumentStep))
          ? 1
          : currentDocumentStep;

      if (typeof correctedStep === "number" && !isNaN(correctedStep)) {
        sessionStorage.setItem("currentDocumentStep", String(correctedStep));
      }

      setAreRoutesLoaded(true);
      sessionStorage.setItem("currentStep", (currentStep + 1).toString());
  };

  // The documents behind "Other Id Card" for this country, supplied by the backend on the flow
  // config. Absent for countries with nothing behind the option — those keep behaving exactly as
  // before, with "Other ID Card" as a plain, directly selectable entry.
  const otherIdDocuments = documentLists?.otherIdDocuments ?? [];
  const hasOtherIdDrillDown = otherIdDocuments.length > 0;

  const filteredOtherIdDocuments = (() => {
    const term = otherIdSearch.trim().toLowerCase();
    if (!term) return otherIdDocuments;
    return otherIdDocuments.filter((document) =>
      document.toLowerCase().includes(term)
    );
  })();

  const showOtherIdSearch = otherIdDocuments.length >= OTHER_ID_SEARCH_THRESHOLD;

  // The card must stay inside the viewport: `.parent-container` is height:100vh with
  // overflow-y:auto, so taller content raises a second, outer scrollbar beside the list's own.
  //
  // The drill-down swaps the 54px country field for a header, plus a search box on long lists:
  //   header:  18px margin + 32px button  =  50   →  50 − 54 ≈ 0 to reclaim
  //   search: + 12px margin + 54px field  = 116   → 116 − 54 =  62 to reclaim
  //
  // So only the searchable case has anything to reclaim, and only from the viewport cap — the
  // fixed desktop height stays the same on both levels, which is what makes them read as one
  // list rather than two. On a short window the cap still wins and shrinks the list.
  const isListHeightReduced = isOtherIdDrillDown && showOtherIdSearch;

  const documentListMaxHeight = isListHeightReduced ? "calc(49vh - 62px)" : "49vh";

  const closeOtherIdDrillDown = () => {
    setIsOtherIdDrillDown(false);
    setOtherIdSearch("");
  };

  const handleDocumentTap = (docSelected) => {
    if (docSelected === OTHER_ID_CARD && hasOtherIdDrillDown) {
      setOtherIdSearch("");
      setIsOtherIdDrillDown(true);
      return;
    }
    setDocTypes(docSelected);
  };

  // Whichever document the applicant picks from the Other ID list, the journey still submits
  // "Other Id Card". The specific pick is presentation only — sending the concrete name would
  // change what the backend matches against and would break prefilled journeys, where the
  // captured idType is compared with the type the journey was created with.
  const handleOtherIdDocumentTap = () => {
    setDocTypes(OTHER_ID_CARD);
  };

  const setDocTypes = async (docSelected) => {
  const { backendIdType } = resolveIndiaNoDoc({
    uiIdType: docSelected,
    documentCapture,
    noDocReplacements,
  });

  // UI state → Digilocker
  setCurrentPageState({ ...currentPageState, idType: docSelected });

  // Journey → backend value (Digilocker / Digilocker 2.0)
  //
  // Filed under this document's own key, matching what one-journey-backend just stored against
  // currentDocumentStep. Writing the unsuffixed `idType` for every document used to leave the
  // capture screens reading document one's type — a passport in the first slot then decided that
  // a later document needed no back side (IDX-677).
  setJourney({
    ...journey,
    capturedData: {
      ...journeyData,
      [getDocumentFieldName("idType", currentDocumentStep)]: backendIdType,
    },
  });
};


  // Watches this document's own type: the mirror above files it under idType2 / idType3, so
  // depending on the unsuffixed `idType` would leave every document after the first watching a
  // value that no longer changes.
  const capturedIdTypeForThisDocument = getDocumentIdType(journey, currentDocumentStep);

  useEffect(() => {
    if (!hasUpdated.current && capturedIdTypeForThisDocument && currentPageState?.idType)
      hasUpdated.current = true;
    updateJourneyAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capturedIdTypeForThisDocument, currentPageState?.idType]);

  // Auto-select the prefilled document type once, driving the same path a tap would.
  useEffect(() => {
    if (autoSelectedRef.current || currentPageState?.idType) return;
    if (willAutoSelectPrefilledDoc) {
      autoSelectedRef.current = true;
      setDocTypes(prefilledDocType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [willAutoSelectPrefilledDoc]);

  // IDX-707: On the Digilocker route the applicant makes no document choice — the operator's flow
  // config is the source. Auto-open the session once (DOC_SELECTION → digilockerUrl) and redirect,
  // so the Document Selection screen is never shown. The backend auto-resolves the ID type from
  // config.digilocker.version, so the value sent here is only a placeholder.
  useEffect(() => {
    if (!config?.digilocker?.required || digilockerRedirectedRef.current) return;
    digilockerRedirectedRef.current = true;
    (async () => {
      // A remount must NOT re-open the session. On the Digilocker route the DOC_SELECTION response
      // triggers a re-orchestration in Settings.updateJourney (status DOC_SELECTION + country IN),
      // which rebuilds the routes and remounts this component. A React ref resets on remount, so
      // guarding only with the ref re-fired DOC_SELECTION on every remount → an update-journey loop
      // that pre-empted the redirect (the flicker/reload). The stored digilockerUrl is the
      // cross-remount guard: if the session was already opened, just redirect to it.
      const existingUrl = sessionStorage.getItem("digilockerUrl");
      if (existingUrl) {
        window.location.replace(existingUrl);
        return;
      }
      try {
        const version = config.digilocker.version;
        sessionStorage.setItem("document", version);
        const response = await updateJourney(
          "DOC_SELECTION",
          { currentDocumentStep: currentDocumentStep + 1, idType: version, verificationType: "NO_DOC" },
          currentStep
        );
        if (response?.digilockerUrl) {
          sessionStorage.setItem("digilockerUrl", response.digilockerUrl);
          window.location.replace(response.digilockerUrl);
          return;
        }
        // Soft-fail: updateJourney swallows transport/journey errors into { error: true } and has
        // already surfaced them (Settings sets journeyExpired / sessionExpired for a 403, or the
        // generic `error` flag otherwise). A transient 5xx / timeout / dropped connection is NOT a
        // DigiLocker verification failure and must not terminate a journey the backend still holds
        // In Progress. Route to the recoverable error screen; for an expired/session error the
        // shell already renders that state over any route. (IDX-707 #1)
        if (response?.error) {
          navigate(`/${journeyId}/error`);
          return;
        }
        // Genuine 200 with no session URL — the backend could not start a DigiLocker session. There
        // is no Document Selection UI to fall back to, and Phase 1 terminates on a DigiLocker
        // failure, so route to the terminated screen with a reason instead of leaving the Loader up.
        throw new Error("Digilocker session URL missing from DOC_SELECTION response");
      } catch (err) {
        console.error("[SelectDocument] Digilocker session open failed", err);
        sessionStorage.setItem(
          "terminationMessage",
          "We couldn't start the DigiLocker session. Please try again later."
        );
        navigate(`/${journeyId}/verification-terminated`);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.digilocker?.required]);

  // Show the loader (not the one-option picker) while the prefilled document auto-advances, or while
  // the Digilocker route auto-redirects (no selection UI).
  if (loading || willAutoSelectPrefilledDoc || config?.digilocker?.required) {
    return <Loader />;
  }
  return (
    <>
      <Head
        showBackIcon={
          isOtherIdDrillDown ||
          (currentDocumentStep === 0 && currentDocumentRetries === 0)
        }
        showCountry={false}
        showLanguages={false}
        handleBack={handleBackButtonClick}
        stepperFunc={stepperFunc}
        showMobileFlow={false}
        style={{ zIndex: "100" }}
      />
      <div
        className="parent-container"
        style={{
          paddingTop: isMobileView ? "70px" : "auto",
        }}
      >
        <div className="main">
          <div
            className="left-section"
            style={{ display: isMobileView ? "none" : "flex" }}
          >
            <div
              style={{
                display: "block",
              }}
            >
              <RenderSvg
                config={config}
                svgType={"selectDocument"}
                id="selectDocument"
              />
            </div>
          </div>
          <div
            className="right-section"
            style={{
              height: isMobileView ? "100vh" : "auto",
              justifyContent: isMobileView ? "space-between" : "flex-end",
              borderRadius: isMobileView ? "0" : "auto",
              boxShadow: isMobileView ? "none" : "auto",
            }}
          >
            <div className="flex flex-col">
              <div className="text-[18px] md:text-[24px] font-semibold text-[#000000] text-center md:text-start md:mb-[32px] mb-[20px]">
                {
                  languageData[language]?.documentationList
                    ?.docHeading
                }
              </div>
              {/* The country field is hidden inside the Other ID list. It reads as a second
                  search bar directly above the document search, and its X reads as "change
                  country" rather than "go back" — the header arrow is the only back
                  affordance there. Hiding it also returns 54px to the list. */}
              {!isOtherIdDrillDown && (
                <div className="search-container flex h-[54px] items-center self-stretch">
                  <MagnifyingGlassIcon className="search-icon w-[24px] h-[24px]" />
                  <input
                    type="text"
                    id="floating_outlined"
                    disabled
                    className="relative peer appearance-none block w-full text-gray-700 border border-gray-200 rounded pl-[36px] pr-[14px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500 inputFields"
                    defaultValue={
                      selectedCountry
                        ? `${selectedCountry.logo} ${selectedCountry.label}`
                        : ""
                    }
                    style={{ borderRadius: "10px" }}
                    key={selectedCountry}
                  />
                  {currentDocumentStep === 0 && currentDocumentRetries === 0 && (
                    <button
                      className="clear-button"
                      onClick={(e) => handleBackButtonClick(e)}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              )}
              {/* Second level: the documents behind "Other ID Card". Same box, same list
                  component — only the header and the contents change. */}
              {isOtherIdDrillDown && (
                <div className="mt-[18px]">
                  <div className="flex items-center gap-[8px]">
                    <button
                      type="button"
                      aria-label="Back to document list"
                      onClick={closeOtherIdDrillDown}
                      className="flex items-center justify-center h-[32px] w-[32px] rounded-full hover:bg-gray-100"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                    <span className="font-semibold text-[16px]">
                      {OTHER_ID_CARD}
                    </span>
                  </div>

                  {showOtherIdSearch && (
                    <div className="search-container flex h-[54px] items-center self-stretch mt-[12px]">
                      <MagnifyingGlassIcon className="search-icon w-[24px] h-[24px]" />
                      <input
                        type="text"
                        value={otherIdSearch}
                        onChange={(e) => setOtherIdSearch(e.target.value)}
                        placeholder="Search documents"
                        className="relative peer appearance-none block w-full text-gray-700 border border-gray-200 rounded pl-[36px] pr-[14px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500 inputFields"
                        style={{ borderRadius: "10px" }}
                      />
                    </div>
                  )}
                </div>
              )}

              {(Boolean(documents.length) || selectedCountry) && (
                <div
                  className="scrollbar md:pr-[8px] pr-0 mt-[18px] mb-[25px] s md:h-[15rem]"
                  style={{ overflowY: "auto", maxHeight: documentListMaxHeight }}
                >
                  {isOtherIdDrillDown ? (
                    filteredOtherIdDocuments.length > 0 ? (
                      <DocumentsList
                        countryCode={selectedCountry?.value}
                        countryName={selectedCountry?.label}
                        onClick={updateJourneyAsync}
                        docSelected={currentPageState?.idType}
                        list={filteredOtherIdDocuments}
                        handleNext={handleOtherIdDocumentTap}
                      />
                    ) : (
                      <p className="text-[14px] text-gray-500 py-[8px]">
                        No documents match “{otherIdSearch.trim()}”
                      </p>
                    )
                  ) : (
                    Boolean(documents.length) && (
                      <DocumentsList
                        countryCode={selectedCountry?.value}
                        countryName={selectedCountry?.label}
                        onClick={updateJourneyAsync}
                        docSelected={currentPageState?.idType}
                        list={documents}
                        handleNext={handleDocumentTap}
                      />
                    )
                  )}
                </div>
              )}
            </div>
            <Footer style={{ marginTop: "16px" }} />
          </div>
        </div>
      </div>
    </>
  );
}

export default SelectDocument;
